<ng-template #searchSlot>
  @if (searchable()) {
    <div class="v-one-table__search">
      <v-one-search
        [placeholder]="searchPlaceholder()"
        [debounceTime]="searchDebounce()"
        (searchChange)="onSearchChange($event)"
      />
    </div>
  }
</ng-template>

<ng-template #filterSlot>
  @if (filterable()) {
    <div class="v-one-table__filters">
      <v-one-filter
        [options]="filterOptions()"
        [placeholder]="filterPlaceholder()"
        [showChips]="showFilterChips()"
        (filterChange)="onFilterChange($event)"
      />
    </div>
  }
</ng-template>

<ng-template #actionsSlot>
  <div class="v-one-table__actions">
    @if (showRegisterButton()) {
      <v-one-button
        [text]="registerLabel()"
        [variant]="'primary'"
        [icon]="registerButtonIcon()"
        [iconType]="ICON_TYPE.OUTLINE"
        [iconPosition]="'right'"
        (buttonClick)="onRegisterClick()"
      />
    }
  </div>
</ng-template>

<div class="v-one-table">
  <!-- Toolbar: search, filter and register button -->
  @if (toolbarLayout() === 'inline') {
    <div class="v-one-table__toolbar">
      <ng-container [ngTemplateOutlet]="searchSlot" />
      <ng-container [ngTemplateOutlet]="filterSlot" />
      <ng-container [ngTemplateOutlet]="actionsSlot" />
    </div>
  } @else {
    <div class="v-one-table__header">
      <ng-container [ngTemplateOutlet]="searchSlot" />
      <ng-container [ngTemplateOutlet]="actionsSlot" />
    </div>

    <ng-container [ngTemplateOutlet]="filterSlot" />
  }

  <!-- Table Container with Horizontal Scroll -->
  <div class="v-one-table__container">
    <div class="v-one-table__scroll-wrapper">
      <!-- Loading State -->
      @if (showsLoadingState()) {
        <div class="v-one-table__loading">
          {{ 'common.loading' | translate }}
        </div>
      }

      <!-- Empty State -->
      @if (!loading() && !hasData()) {
        <div class="v-one-table__empty">
          {{ 'common.noResults' | translate }}
        </div>
      }

      <!-- Table -->
      @if (showsTable()) {
        <table
          class="v-one-table__table"
          [class.v-one-table__table--reloading]="loading()"
          [style.min-width]="tableMinWidth() || null"
        >
          <thead class="v-one-table__thead">
            <tr class="v-one-table__header-row">
              <!-- Multiselect Checkbox Column -->
              @if (tableVariant() === 'multiselect') {
                <th class="v-one-table__th v-one-table__th--checkbox">
                  <input
                    type="checkbox"
                    class="v-one-table__checkbox"
                    [checked]="allSelected()"
                    (change)="toggleSelectAll()"
                    [attr.aria-label]="'Select all'"
                  />
                </th>
              }

              <!-- Expandable Column -->
              @if (tableVariant() === 'expandable') {
                <th class="v-one-table__th v-one-table__th--expand"></th>
              }

              <!-- Data Columns -->
              @for (column of columns(); track column.key) {
                <th
                  class="v-one-table__th"
                  [class.v-one-table__th--sortable]="column.sortable"
                  [class.v-one-table__th--sorted]="isSortActive(column)"
                  [class.v-one-table__th--left]="
                    column.align === 'left' || !column.align
                  "
                  [class.v-one-table__th--center]="column.align === 'center'"
                  [class.v-one-table__th--right]="column.align === 'right'"
                  [style.width]="column.width || 'auto'"
                  [style.height]="'45px'"
                  (click)="onSort(column)"
                >
                  <div class="v-one-table__th-content">
                    <span class="v-one-table__th-label">{{
                      column.label
                    }}</span>

                    <!-- Header Icons -->
                    @if (column.headerIcons && column.headerIcons.length > 0) {
                      <div class="v-one-table__th-icons">
                        @for (
                          headerIcon of column.headerIcons;
                          track headerIcon.type
                        ) {
                          <button
                            type="button"
                            class="v-one-table__th-icon-btn"
                            [title]="headerIcon.tooltip || ''"
                            (click)="onHeaderIconClick(headerIcon, $event)"
                          >
                            <v-one-icon
                              [name]="headerIcon.icon"
                              [type]="ICON_TYPE.FILLED"
                            />
                          </button>
                        }
                      </div>
                    }

                    <!-- Sort Icon -->
                    @if (column.sortable) {
                      <v-one-icon
                        [name]="getSortIcon(column)"
                        [type]="ICON_TYPE.FILLED"
                        class="v-one-table__sort-icon"
                        [class.v-one-table__sort-icon--active]="
                          isSortActive(column)
                        "
                      />
                    }
                  </div>
                </th>
              }

              <!-- Actions Column -->
              @if (hasActions()) {
                <th class="v-one-table__th v-one-table__th--actions">
                  @if (actionsLabel()) {
                    {{ actionsLabel() | translate }}
                  }
                </th>
              }
            </tr>
          </thead>

          <tbody class="v-one-table__tbody">
            @for (
              item of data();
              track trackByFn($index, item);
              let i = $index
            ) {
              <tr class="v-one-table__row">
                <!-- Multiselect Checkbox Cell -->
                @if (tableVariant() === 'multiselect') {
                  <td class="v-one-table__td v-one-table__td--checkbox">
                    <input
                      type="checkbox"
                      class="v-one-table__checkbox"
                      [checked]="isSelected(item)"
                      (change)="toggleSelection(item)"
                      [attr.aria-label]="'Select row ' + (i + 1)"
                    />
                  </td>
                }

                <!-- Expandable Cell -->
                @if (tableVariant() === 'expandable') {
                  <td class="v-one-table__td v-one-table__td--expand">
                    <button
                      type="button"
                      class="v-one-table__expand-btn"
                      (click)="toggleExpand(item)"
                      [attr.aria-label]="
                        isExpanded(item) ? 'Collapse row' : 'Expand row'
                      "
                      [attr.aria-expanded]="isExpanded(item)"
                    >
                      <v-one-icon
                        [name]="isExpanded(item) ? 'chevronUp' : 'chevronDown'"
                        [type]="ICON_TYPE.OUTLINE"
                      />
                    </button>
                  </td>
                }

                <!-- Data Cells -->
                @for (column of columns(); track column.key) {
                  @if (column.cellConfig) {
                    <!-- Cell with variant configuration -->
                    <td
                      class="v-one-table__td v-one-table__td--variant"
                      [class.v-one-table__td--left]="
                        column.align === 'left' || !column.align
                      "
                      [class.v-one-table__td--center]="
                        column.align === 'center'
                      "
                      [class.v-one-table__td--right]="column.align === 'right'"
                    >
                      <v-one-table-cell
                        [config]="getCellConfig(item, column)"
                      />
                    </td>
                  } @else {
                    <!-- Standard cell rendering -->
                    <td
                      class="v-one-table__td"
                      [class.v-one-table__td--left]="
                        column.align === 'left' || !column.align
                      "
                      [class.v-one-table__td--center]="
                        column.align === 'center'
                      "
                      [class.v-one-table__td--right]="column.align === 'right'"
                    >
                      @switch (column.type) {
                        @case ('currency') {
                          <span>
                            {{
                              getCellValue(item, column)
                                | currency: 'USD' : 'symbol' : '1.2-2'
                            }}
                          </span>
                        }
                        @case ('date') {
                          <span>
                            {{ getCellValue(item, column) | date: 'short' }}
                          </span>
                        }
                        @case ('percentage') {
                          <span>
                            {{ getCellValue(item, column) | percent: '1.2-2' }}
                          </span>
                        }
                        @case ('number') {
                          <span>
                            {{ getCellValue(item, column) | number: '1.0-2' }}
                          </span>
                        }
                        @case ('select') {
                          <select
                            class="v-one-table__cell-select"
                            (change)="
                              onCellEdit(
                                item,
                                column,
                                $any($event.target).value
                              )
                            "
                          >
                            @for (
                              option of column.selectOptions;
                              track option.value
                            ) {
                              <option [value]="option.value">
                                {{ option.label }}
                              </option>
                            }
                          </select>
                        }
                        @case ('input') {
                          <input
                            type="text"
                            class="v-one-table__cell-input"
                            [value]="getCellValue(item, column)"
                            (blur)="
                              onCellEdit(
                                item,
                                column,
                                $any($event.target).value
                              )
                            "
                          />
                        }
                        @default {
                          <span>
                            {{ getCellValue(item, column) }}
                          </span>
                        }
                      }
                    </td>
                  }
                }

                <!-- Actions Cell -->
                @if (hasActions()) {
                  <td class="v-one-table__td v-one-table__td--actions">
                    <div class="v-one-table__actions-container">
                      @for (action of actions(); track action.icon) {
                        @if (shouldShowAction(action, item)) {
                          <!-- Tooltip Host: takes the hover a disabled
                          button cannot receive -->
                          <span
                            class="v-one-table__action-tooltip"
                            [class.v-one-table__action-tooltip--disabled]="
                              isActionDisabled(action, item)
                            "
                            [class.v-one-table__action-tooltip--link]="
                              action.display === 'link'
                            "
                            [appTooltip]="disabledTooltip(action, item)"
                            tooltipSeverity="default"
                          >
                            @if (action.display === 'link') {
                              <button
                                type="button"
                                class="v-one-table__action-link"
                                [disabled]="isActionDisabled(action, item)"
                                [title]="action.tooltip || ''"
                                (click)="onActionClick(action, item)"
                              >
                                {{ action.label | translate }}
                              </button>
                            } @else {
                              <button
                                type="button"
                                class="v-one-table__action-btn"
                                [class]="
                                  'v-one-table__action-btn--' +
                                  (action.color || 'primary')
                                "
                                [disabled]="isActionDisabled(action, item)"
                                [title]="action.tooltip || ''"
                                (click)="onActionClick(action, item)"
                              >
                                <v-one-icon
                                  [name]="action.icon"
                                  [type]="ICON_TYPE.OUTLINE"
                                />
                              </button>
                            }
                          </span>
                        }
                      }
                    </div>
                  </td>
                }
              </tr>

              <!-- Expandable Row Content -->
              @if (tableVariant() === 'expandable' && isExpanded(item)) {
                <tr class="v-one-table__expanded-row">
                  <td
                    [attr.colspan]="getColspan()"
                    class="v-one-table__expanded-content"
                  >
                    <div class="v-one-table__expanded-inner">
                      <!-- Expandable content goes here -->
                      @if (expandedTemplate()) {
                        <ng-container
                          *ngTemplateOutlet="
                            expandedTemplate()!;
                            context: { $implicit: item }
                          "
                        ></ng-container>
                      }
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      }
    </div>
  </div>

  <!-- Pagination -->
  @if (pageable() && hasData()) {
    <div class="v-one-table__pagination">
      <v-one-paginator
        [totalItems]="totalItems()"
        [currentPage]="currentPage()"
        [itemsPerPage]="pageSize()"
        [variant]="'advanced'"
        [pageSizeOptions]="pageSizeOptions()"
        (pageChange)="onPageChange($event)"
        (itemsPerPageChange)="onItemsPerPageChange($event)"
      />
    </div>
  }
</div>
